import React, { useState, useEffect, useRef, Suspense } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { cn } from '@/lib/utils';
import 'katex/dist/katex.min.css';
import Latex from 'react-latex-next';
import { Play, Pause, RotateCcw, Volume2, VolumeX, BookOpen, Mic, Activity, Quote, TrendingUp, GraduationCap, Loader2, Maximize2, X, MonitorPlay } from 'lucide-react';
import { AIOutput, Step, prepareVideoData } from '@/lib/gemini';
import { formatLatexString, cleanCaptionText, cleanForSpeech } from '@/lib/latexUtils';
import { normalizeBoardContentLines } from '@/lib/layoutEngine';
import { isValidRenderableImageUrl } from '@/lib/visualUtils';
import { Canvas } from '@react-three/fiber';
import { Environment, ContactShadows, OrbitControls } from '@react-three/drei';
import { Avatar2D } from './Avatar2D';
import { StructuredSolutionArea } from './StructuredSolutionArea';

import { ErrorBoundary } from './ErrorBoundary';

interface VideoPlayerProps {
  data: AIOutput;
  doubtData?: AIOutput | null;
  language?: string;
  onReset?: () => void;
  onVideoEnd?: () => void;
}

const getLangCode = (lang: string) => {
  const map: Record<string, string> = {
    "English": "en-US",
    "Hindi": "hi-IN",
    "Bengali": "bn-IN",
    "Gujarati": "gu-IN",
    "Kannada": "kn-IN",
    "Malayalam": "ml-IN",
    "Marathi": "mr-IN",
    "Odia": "or-IN",
    "Punjabi": "pa-IN",
    "Tamil": "ta-IN",
    "Telugu": "te-IN",
    "Spanish": "es-ES",
    "French": "fr-FR",
    "German": "de-DE",
    "Mandarin": "zh-CN",
    "Japanese": "ja-JP",
    "Korean": "ko-KR",
    "Arabic": "ar-SA",
    "Portuguese": "pt-PT",
    "Russian": "ru-RU",
    "Italian": "it-IT"
  };
  return map[lang] || "en-US";
};

export function VideoPlayer({ data, doubtData, language = "English", onReset, onVideoEnd }: VideoPlayerProps) {
  const [activeData, setActiveData] = useState<AIOutput>(() => prepareVideoData(data));

  useEffect(() => {
    if (doubtData) {
      // Clear previous audio
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current = null;
      }
      if ('speechSynthesis' in window) {
        window.speechSynthesis.cancel();
      }

      setActiveData(prepareVideoData(doubtData));
      setCurrentStepIndex(0);
      setCurrentAudioTime(0);
      setCurrentWordCharIndex(0);
      setHasStarted(true);
      setIsPlaying(true);
      isPlayingRef.current = true;
      hasIntroPlayedRef.current = false; 
      
      // Small delay to ensure state updates before playStep
      setTimeout(() => {
        playStep(0, 0, true);
      }, 100);
    }
  }, [doubtData]);

  useEffect(() => {
    if (activeData && hasStarted && isPlaying && currentStepIndex === 0 && currentAudioTime === 0) {
      playStep(0, 0, true);
    }
  }, [activeData]);

  // Update activeData if main data changes (new question)
  useEffect(() => {
    if (data) {
      setActiveData(prepareVideoData(data));
    }
    reset();
  }, [data]);

  const [currentStepIndex, setCurrentStepIndex] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [hasStarted, setHasStarted] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const [isDiagramExpanded, setIsDiagramExpanded] = useState(false);
  const textAreaRef = useRef<HTMLDivElement>(null);
  
  // Single Playback Rule for Intro
  const hasIntroPlayedRef = useRef(false);

  const [stepDurations, setStepDurations] = useState<number[]>([]);
  const [globalTime, setGlobalTime] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const [dragPercentage, setDragPercentage] = useState(0);
  
  useEffect(() => {
    // Estimate durations based on text length
    const estimated = activeData.steps.map(step => {
      const rawText = step.audio_explanation || step.explanation || "";
      const text = cleanForSpeech(rawText);
      const wordCount = text.split(/\s+/).filter(w => w.length > 0).length;
      
      // Determine language characteristics for better estimation
      const isIndic = /[\u0900-\u097F]/.test(text); 
      const isCJK = /[\u4e00-\u9fa5\u3040-\u30ff\uac00-\ud7af]/.test(text); 
      
      let wordsPerSecond = 2.25; // Default for English (~1.25x faster than original 1.8)
      if (isIndic) wordsPerSecond = 1.625; // Slower for Indic languages (~1.25x faster than original 1.3)
      if (isCJK) wordsPerSecond = 3.125; // (~1.25x faster than original 2.5)
      
      return Math.max(3, wordCount / wordsPerSecond + 1.0); // Reduced buffer and minimum
    });
    setStepDurations(estimated);
  }, [activeData]);
  
  const [isLoadingAudio, setIsLoadingAudio] = useState(false);
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [currentWordCharIndex, setCurrentWordCharIndex] = useState(0);
  const lastBoundaryUpdateRef = useRef<number>(Date.now());
  const utteranceRef = useRef<SpeechSynthesisUtterance | null>(null);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const [stepAudioUrls, setStepAudioUrls] = useState<Record<number, string>>({});
  const isElevenLabsFailedRef = useRef(false);
  const isRequestingAudioRef = useRef<Record<number, boolean>>({});
  const inFlightAudioRequestsRef = useRef<Record<number, Promise<string | null>>>({});
  const voiceLockedRef = useRef(false);
  const selectedVoiceRef = useRef<'elevenlabs' | 'system' | null>(null);
  const lockedElevenLabsVoiceIdRef = useRef<string | null>(null);
  const speechQueueRef = useRef<string[]>([]);
  const currentChunkIndexRef = useRef<number>(0);
  const chunkCharOffsetsRef = useRef<number[]>([]);
  
  const isPlayingRef = useRef(isPlaying);
  const currentStepIndexRef = useRef(currentStepIndex);
  const isMutedRef = useRef(isMuted);
  const whiteboardRef = useRef<HTMLDivElement>(null);
  const playerRef = useRef<HTMLDivElement>(null);
  const avatarVideoRef = useRef<HTMLVideoElement>(null);

  // Custom play/pause controller to ensure educational video status perfectly mirrors solution playback state
  useEffect(() => {
    const video = avatarVideoRef.current;
    if (video) {
      if (isPlaying) {
        if (video.paused) {
          video.play().catch(err => console.log('[VideoPlayer] Video play waiting for gesture/ready:', err));
        }
      } else {
        if (!video.paused) {
          video.pause();
        }
      }
    }
    
    if (audioRef.current) {
      if (isPlaying) {
        audioRef.current.play().catch(() => {});
      } else {
        audioRef.current.pause();
      }
    }
  }, [isPlaying]);

  useEffect(() => {
    isPlayingRef.current = isPlaying;
  }, [isPlaying]);

  useEffect(() => {
    currentStepIndexRef.current = currentStepIndex;
  }, [currentStepIndex]);

  useEffect(() => {
    isMutedRef.current = isMuted;
    if (audioRef.current) {
      audioRef.current.volume = isMuted ? 0 : 1;
    }
  }, [isMuted]);

  const getElevenLabsAudio = async (text: string, index: number): Promise<string | null> => {
    if (stepAudioUrls[index]) return stepAudioUrls[index];
    if (inFlightAudioRequestsRef.current[index]) {
      return await inFlightAudioRequestsRef.current[index];
    }

    const cleanText = cleanForSpeech(text, language);
    if (!cleanText || cleanText.trim().length === 0) return null;

    const requestPromise = (async (): Promise<string | null> => {
      // 1. Primary route: Call server-side unified /api/tts route (Sarvam AI for Indian languages, ElevenLabs & Google fallback)
      try {
        const res = await fetch("/api/tts", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            text: cleanText,
            language: language,
            stepIndex: index
          })
        });

        const contentType = res.headers.get("content-type") || "";
        if (res.ok && (contentType.includes("audio/") || contentType.includes("application/octet-stream"))) {
          const blob = await res.blob();
          if (blob && blob.size > 100) {
            const url = URL.createObjectURL(blob);
            setStepAudioUrls(prev => ({ ...prev, [index]: url }));
            return url;
          }
        } else {
          console.warn("[VideoPlayer] /api/tts returned non-audio response:", res.status, contentType);
        }
      } catch (apiErr) {
        console.warn("[VideoPlayer] Backend /api/tts endpoint call failed, attempting direct client fallback:", apiErr);
      }

      // 2. Direct client-side ElevenLabs fallback if /api/tts fails or is unreachable
      if (isElevenLabsFailedRef.current) {
        return null;
      }

      try {
        let rawApiKey = (import.meta as any).env.VITE_ELEVENLABS_API_KEY || (import.meta as any).env.ELEVENLABS_API_KEY || "sk_320b81a43e6c58d00c5ea968e7288194ce54b7877f6f952c";
        const fallbackKey = "sk_320b81a43e6c58d00c5ea968e7288194ce54b7877f6f952c";
        if (rawApiKey) {
          const trimmed = rawApiKey.trim();
          if (trimmed === "" || trimmed.includes("•") || trimmed.includes("*") || trimmed.includes("...") || [...trimmed].some(c => c.charCodeAt(0) > 127)) {
            rawApiKey = fallbackKey;
          } else {
            rawApiKey = trimmed;
          }
        } else {
          rawApiKey = fallbackKey;
        }
        const defaultCandidates = [
          "pNInz6obpgDQGcFmaJgB", // Adam (Bold, clear, calm, deep male narration - Priority 1)
          "JBFqnCBsd6RMkjVDRZzb", // George (Warm, calm, authoritative deep male teacher)
          "bIHbv24MWmeRgasZH58o", // Marcus (Deep, resonant, bold male)
          "onwK4e9ZLuTAKqWW03F9", // Daniel (Authoritative British deep male)
          "TX3LPaxmHKxFdv7VOQHJ", // Liam (Articulate, clear, calm male)
          "VR6AewLTigWG4xSOukaG", // Arnold (Crisp, authoritative male)
          "ErXwobaYiN019PkySvjV"  // Antoni (Well-rounded, calm, pleasant clear male)
        ];
        
        const voiceCandidates = lockedElevenLabsVoiceIdRef.current
          ? [lockedElevenLabsVoiceIdRef.current, ...defaultCandidates.filter(v => v !== lockedElevenLabsVoiceIdRef.current)]
          : defaultCandidates;

        let response: Response | null = null;
        let successfulVoiceId = "";

        for (const vId of voiceCandidates) {
          const res = await fetch(`https://api.elevenlabs.io/v1/text-to-speech/${vId}`, {
            method: 'POST',
            headers: {
              'Accept': 'audio/mpeg',
              'Content-Type': 'application/json',
              'xi-api-key': rawApiKey
            },
            body: JSON.stringify({
              text: cleanText,
              model_id: "eleven_multilingual_v2",
              voice_settings: { 
                stability: 0.80, 
                similarity_boost: 0.85, 
                style: 0.0, 
                use_speaker_boost: true 
              }
            })
          });

          if (res.ok) {
            response = res;
            successfulVoiceId = vId;
            if (!lockedElevenLabsVoiceIdRef.current) {
              lockedElevenLabsVoiceIdRef.current = vId;
              console.log("Locked ElevenLabs Voice Candidate:", vId);
            }
            break;
          }
        }

        if (!response || !response.ok) {
          isElevenLabsFailedRef.current = true;
          return null;
        }
        const blob = await response.blob();
        const url = URL.createObjectURL(blob);
        setStepAudioUrls(prev => ({ ...prev, [index]: url }));
        return url;
      } catch (err) {
        isElevenLabsFailedRef.current = true;
        return null;
      }
    })();

    inFlightAudioRequestsRef.current[index] = requestPromise;
    try {
      return await requestPromise;
    } finally {
      delete inFlightAudioRequestsRef.current[index];
    }
  };

  // Cleanup on unmount
  useEffect(() => {
    // Pre-load voices for some browsers
    if ('speechSynthesis' in window) {
      window.speechSynthesis.getVoices();
      const handleVoices = () => window.speechSynthesis.getVoices();
      window.speechSynthesis.onvoiceschanged = handleVoices;
      
      // Auto-scrolling for captions
      const scrollCaptions = () => {
        const container = document.getElementById('explanation-scroll-container');
        const activeWord = document.getElementById('active-word');
        if (container && activeWord) {
          const containerHeight = container.clientHeight;
          const wordTop = activeWord.offsetTop;
          const wordHeight = activeWord.offsetHeight;
          
          // Smooth scroll to center the active word
          container.scrollTo({
            top: wordTop - (containerHeight / 2) + (wordHeight / 2),
            behavior: 'smooth'
          });
        }
      };

      // Call immediately and when voices change
      scrollCaptions();

      return () => {
        window.speechSynthesis.onvoiceschanged = null;
        window.speechSynthesis.cancel();
      };
    }
  }, []);

  useEffect(() => {
    // Auto-scroll explanation box when word index changes
    const scrollCaptions = () => {
      const container = document.getElementById('explanation-scroll-container');
      const activeWord = document.getElementById('active-word');
      if (container && activeWord) {
        const containerHeight = container.clientHeight;
        const wordTop = activeWord.offsetTop;
        const wordHeight = activeWord.offsetHeight;
        
        container.scrollTo({
          top: wordTop - (containerHeight / 2) + (wordHeight / 2),
          behavior: 'smooth'
        });
      }
    };
    
    // Use requestAnimationFrame for smoothness
    const timeout = setTimeout(scrollCaptions, 50);
    return () => clearTimeout(timeout);
  }, [currentWordCharIndex]);

  useEffect(() => {
    // No global scaling needed as we now use fixed font size and wrapping
  }, [data]);

  useEffect(() => {
    const loadVoices = () => {
      const voices = window.speechSynthesis.getVoices();
      if (voices.length > 0) {
        // Voices are loaded
      }
    };
    
    loadVoices();
    if ('speechSynthesis' in window) {
      window.speechSynthesis.onvoiceschanged = loadVoices;
    }
    
    return () => {
      if ('speechSynthesis' in window) {
        window.speechSynthesis.onvoiceschanged = null;
      }
    };
  }, []);

  const [currentAudioTime, setCurrentAudioTime] = useState(0);
  const [failedImages, setFailedImages] = useState<Set<string>>(new Set());

  const handleImageError = (url: string) => {
    setFailedImages(prev => {
      const next = new Set(prev);
      next.add(url);
      return next;
    });
  };

  const getHeroVisual = (index: number) => {
    const step = activeData.steps[index];
    if (step?.diagram_svg && step.diagram_svg.toLowerCase().includes('<svg')) {
      return { 
        type: 'svg' as const, 
        value: step.diagram_svg, 
        description: step.diagram_description || step.reference_text || activeData.diagram_description 
      };
    }
    const stepImg = (step as any)?.image_url || (step as any)?.static_image_url;
    if (stepImg && isValidRenderableImageUrl(stepImg) && !failedImages.has(stepImg)) {
      return {
        type: 'image' as const,
        value: stepImg,
        description: step.diagram_description || step.reference_text || activeData.diagram_description
      };
    }
    if (activeData.static_image_url && isValidRenderableImageUrl(activeData.static_image_url) && !failedImages.has(activeData.static_image_url)) {
      return { 
        type: 'image' as const, 
        value: activeData.static_image_url, 
        description: activeData.diagram_description 
      };
    }
    return null;
  };

  const getPersistentVisual = (index: number) => {
    // Find the latest visual available up to the current step
    for (let i = index; i >= 0; i--) {
      const step = activeData.steps[i];
      if (step?.diagram_svg && step.diagram_svg.toLowerCase().includes('<svg')) {
        return { 
          type: 'svg' as const, 
          value: step.diagram_svg, 
          description: step.diagram_description || step.reference_text || activeData.diagram_description 
        };
      }
      const stepImg = (step as any)?.image_url || (step as any)?.static_image_url;
      if (stepImg && isValidRenderableImageUrl(stepImg) && !failedImages.has(stepImg)) {
        return {
          type: 'image' as const,
          value: stepImg,
          description: step.diagram_description || step.reference_text || activeData.diagram_description
        };
      }
    }
    
    // Fallback to global static image if we are past intro
    if (activeData.static_image_url && isValidRenderableImageUrl(activeData.static_image_url) && !failedImages.has(activeData.static_image_url) && index >= 0) {
      return { 
        type: 'image' as const, 
        value: activeData.static_image_url, 
        description: activeData.diagram_description 
      };
    }
    
    return null;
  };

  const currentHeroVisual = getHeroVisual(currentStepIndex);
  const persistentVisual = getPersistentVisual(currentStepIndex);
  
  const getShowcaseDuration = (index: number) => {
    const duration = stepDurations[index] || 5;
    return Math.min(10, Math.max(5, duration));
  };

  const currentShowcaseDuration = getShowcaseDuration(currentStepIndex);
  
  // A visual is showcased on the Main Board if:
  // 1. The step is a dedicated visual frame containing an SVG diagram or valid step image
  // 2. Or we are in the overview step with a static valid image
  const currentStepImg = (activeData.steps[currentStepIndex] as any)?.image_url || (activeData.steps[currentStepIndex] as any)?.static_image_url;
  const hasValidStepImg = currentStepImg && isValidRenderableImageUrl(currentStepImg) && !failedImages.has(currentStepImg);
  const hasValidGlobalImg = activeData.static_image_url && isValidRenderableImageUrl(activeData.static_image_url) && !failedImages.has(activeData.static_image_url);

  const isVisualShowcase = hasStarted && (
    (!!activeData.steps[currentStepIndex]?.diagram_svg && activeData.steps[currentStepIndex].diagram_svg.toLowerCase().includes('<svg')) || 
    Boolean(hasValidStepImg) ||
    (Boolean(hasValidGlobalImg) && currentStepIndex === 1 && !activeData.steps[1]?.diagram_svg)
  );

  const playStep = async (index: number, startTime: number = 0, shouldPlay: boolean = true) => {
    const step = activeData.steps[index];
    if (!step) return;

    if ('speechSynthesis' in window) {
      window.speechSynthesis.cancel();
    }
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.onended = null;
      audioRef.current.ontimeupdate = null;
      audioRef.current = null;
    }
    
    isPlayingRef.current = isPlaying;
    currentStepIndexRef.current = index;
    setCurrentAudioTime(startTime);
    setCurrentWordCharIndex(0);

    if (!isPlayingRef.current && shouldPlay) {
      setIsPlaying(true);
      isPlayingRef.current = true;
    }
    
    if (!isPlayingRef.current || currentStepIndexRef.current !== index) {
      if (!shouldPlay) setCurrentAudioTime(startTime);
      return;
    }
    
    const rawText = step.audio_explanation || step.explanation;
    const fullText = cleanForSpeech(rawText, language);

    // VOICE LOCK LOGIC:
    // If we've already chosen a voice type for this session, stick to it.
    // If not, try ElevenLabs once. If it fails, lock to System forever.
    
    let audioUrl = stepAudioUrls[index] || null;

    if (selectedVoiceRef.current === 'elevenlabs' || (selectedVoiceRef.current === null && !isElevenLabsFailedRef.current)) {
      if (!audioUrl && !isElevenLabsFailedRef.current) {
        try {
          audioUrl = await getElevenLabsAudio(fullText, index);
        } catch (err) {
          console.error("ElevenLabs critical failure in playStep:", err);
          // Fallback handled below by checking audioUrl
        }
      }
      
      if (audioUrl && !isElevenLabsFailedRef.current) {
        if (selectedVoiceRef.current === null) {
          selectedVoiceRef.current = 'elevenlabs';
          console.log("Voice System Locked: ElevenLabs");
        }
      } else {
        // Only switch to system if we haven't locked to ElevenLabs yet or if it just failed
        if (selectedVoiceRef.current !== 'elevenlabs') {
          selectedVoiceRef.current = 'system';
          console.warn("Voice System Locked: System Fallback");
        }
      }
    }

    // Check again after await
    if (!isPlayingRef.current || currentStepIndexRef.current !== index) {
      return;
    }

    if (audioUrl && selectedVoiceRef.current === 'elevenlabs') {
      const audio = new Audio(audioUrl);
      audioRef.current = audio;
      audio.volume = isMutedRef.current ? 0 : 1;
      
      if (startTime > 0) audio.currentTime = startTime;

      audio.onplay = () => {
        setIsSpeaking(true);
        setIsLoadingAudio(false);
      };
      audio.onended = () => {
        if (isPlayingRef.current && currentStepIndexRef.current === index) {
          if (index === 0) {
            hasIntroPlayedRef.current = true;
          }
          if (currentStepIndexRef.current < activeData.steps.length - 1) {
            const nextIdx = currentStepIndexRef.current + 1;
            setCurrentStepIndex(nextIdx);
            playStep(nextIdx);
          } else {
            setIsPlaying(false);
            setIsSpeaking(false);
            onVideoEnd?.();
          }
        }
      };

      audio.ontimeupdate = () => {
        if (audio.duration) {
          const progress = audio.currentTime / audio.duration;
          setCurrentWordCharIndex(Math.floor(progress * fullText.length));
          setCurrentAudioTime(audio.currentTime);
        }
      };

      audio.play().catch(console.error);
      return;
    }

    if ('speechSynthesis' in window) {
      const duration = stepDurations[index] || 5;
      
      // Chunking: Split by sentences to prevent timeouts and improve reliability
      // We use a regex that preserves the spaces to keep character offsets accurate
      const chunks: string[] = [];
      const chunkOffsets: number[] = [];
      let lastIndex = 0;
      // Enhanced regex for better sentence splitting across languages while respecting decimals
      const chunkRegex = /[^.!?।॥؟。\n,;]+(?:(?![.!?।॥؟。\n,;](?:\s|$))[.!?।॥؟。\n,;][^.!?।॥؟。\n,;]*)*[.!?।॥؟。\n,;]*(?:\s+|$)/g;
      let match;
      
      while ((match = chunkRegex.exec(fullText)) !== null) {
        let chunk = match[0].trim();
        if (chunk.length === 0) continue;

        // If chunk is too long for a single speech utterance, split it further
        if (chunk.length > 150) {
           const subChunks = chunk.split(/[,，、]/);
           let currentSub = "";
           let subOffset = match.index;
           for (const sc of subChunks) {
             const trimmedSc = sc.trim();
             if ((currentSub + trimmedSc).length > 150 && currentSub.length > 0) {
               chunks.push(currentSub);
               chunkOffsets.push(subOffset);
               subOffset += currentSub.length;
               currentSub = trimmedSc;
             } else {
               currentSub += (currentSub.length > 0 ? " " : "") + trimmedSc;
             }
           }
           if (currentSub.length > 0) {
             chunks.push(currentSub);
             chunkOffsets.push(subOffset);
           }
        } else {
          chunks.push(chunk);
          chunkOffsets.push(match.index);
        }
        lastIndex = chunkRegex.lastIndex;
      }
      
      // Add remaining text if any
      if (lastIndex < fullText.length) {
        chunks.push(fullText.substring(lastIndex));
        chunkOffsets.push(lastIndex);
      }
      
      if (chunks.length === 0 && fullText.length > 0) {
        chunks.push(fullText);
        chunkOffsets.push(0);
      }

      // Final strict pass: Ensure NO chunk exceeds 120 characters to perfectly bypass the Web Speech 14-second bug.
      const finalChunks: string[] = [];
      const finalOffsets: number[] = [];
      for (let i = 0; i < chunks.length; i++) {
        let chunk = chunks[i];
        let offset = chunkOffsets[i];
        if (chunk.length > 120) {
           let current = "";
           let currentOffset = offset;
           const tokens = chunk.split(/(\s+)/);
           for (const t of tokens) {
              if (t.trim().length === 0) {
                 if (current.length > 0) {
                    current += t;
                 } else {
                    currentOffset += t.length;
                 }
                 continue;
              }
              if ((current + t).length > 120 && current.length > 0) {
                 finalChunks.push(current);
                 finalOffsets.push(currentOffset);
                 currentOffset += current.length;
                 current = t;
              } else {
                 current += t;
              }
           }
           if (current.length > 0) {
             finalChunks.push(current);
             finalOffsets.push(currentOffset);
           }
        } else {
           finalChunks.push(chunk);
           finalOffsets.push(offset);
        }
      }

      // Trim chunks but keep offsets aligned to the start of the first non-whitespace char.
      const cleanedChunks: string[] = [];
      const cleanedOffsets: number[] = [];
      for (let i = 0; i < finalChunks.length; i++) {
         const originalChunk = finalChunks[i];
         const trimmedStartMatch = originalChunk.match(/^\s+/);
         const leadingSpacesLength = trimmedStartMatch ? trimmedStartMatch[0].length : 0;
         
         const trimmedChunk = originalChunk.trim();
         if (trimmedChunk.length > 0) {
            cleanedChunks.push(trimmedChunk);
            cleanedOffsets.push(finalOffsets[i] + leadingSpacesLength);
         }
      }
      
      // Reassign to work seamlessly with existing logic
      chunks.length = 0;
      chunkOffsets.length = 0;
      chunks.push(...cleanedChunks);
      chunkOffsets.push(...cleanedOffsets);

      speechQueueRef.current = chunks;
      currentChunkIndexRef.current = 0;
      chunkCharOffsetsRef.current = chunkOffsets;

      const playChunk = (chunkIdx: number, startFromChar: number = 0) => {
        if (!isPlayingRef.current || currentStepIndexRef.current !== index || chunkIdx >= chunks.length) {
          if (chunkIdx >= chunks.length && isPlayingRef.current) {
            // Step finished
            const currentDuration = duration || stepDurations[index] || 0;
            const minShowcase = (index === 0) ? 3 : 1;
            const remainingDelay = (index === 0 || activeData.steps[index]?.diagram_svg) ? Math.max(0, minShowcase - currentDuration) : 0;

            setTimeout(() => {
              if (isPlayingRef.current && currentStepIndexRef.current === index) {
                // EXTREME SAFETY: If the speech engine is STILL speaking (e.g. slow language), 
                // wait for it rather than cutting it off.
                const checkFinished = () => {
                  if (window.speechSynthesis.speaking && isPlayingRef.current) {
                    setTimeout(checkFinished, 200);
                  } else {
                    if (index === 0) {
                      hasIntroPlayedRef.current = true;
                    }
                    if (currentStepIndexRef.current < activeData.steps.length - 1) {
                      const nextIndex = currentStepIndexRef.current + 1;
                      setCurrentStepIndex(nextIndex);
                      playStep(nextIndex);
                    } else {
                      setIsPlaying(false);
                      onVideoEnd?.();
                    }
                  }
                };
                checkFinished();
              }
            }, remainingDelay * 1000);
          }
          return;
        }

        currentChunkIndexRef.current = chunkIdx;
        let chunkText = chunks[chunkIdx];
        
        if (!chunkText || chunkText.trim().length === 0) {
          if (isPlayingRef.current && currentStepIndexRef.current === index) {
            playChunk(chunkIdx + 1);
          }
          return;
        }

        let localCharOffset = 0;

        // Handle resuming within a chunk
        if (startFromChar > 0) {
          const words = chunkText.split(/\s+/);
          const progress = startFromChar / chunkText.length;
          const skipWords = Math.floor(progress * words.length);
          if (skipWords < words.length) {
            const skippedText = words.slice(0, skipWords).join(' ');
            localCharOffset = skippedText.length + (skippedText.length > 0 ? 1 : 0);
            chunkText = words.slice(skipWords).join(' ');
          }
        }

        const utterance = new SpeechSynthesisUtterance(chunkText);
        utteranceRef.current = utterance; // Keep reference to prevent GC
        
        utterance.lang = getLangCode(language);
        utterance.volume = isMutedRef.current ? 0 : 1;
        // Pacing and deep bold pitch for male delivery across languages
        const langCode = getLangCode(language);
        const isIndicCode = langCode.startsWith('hi') || langCode.startsWith('bn') || langCode.startsWith('mr') || langCode.startsWith('ta') || langCode.startsWith('te');
        utterance.rate = isIndicCode ? 0.92 : 0.95;
        utterance.pitch = 0.78; // Calm, clear, bold, deep masculine pitch
        
        let voices = window.speechSynthesis.getVoices();
        if (voices.length === 0) {
          voices = window.speechSynthesis.getVoices();
        }
        
        const langPrefix = langCode.split('-')[0];
        let searchPrefix = langPrefix;
        
        // Strict classification helpers: Never allow female voices
        const isFemaleVoiceName = (name: string) => {
          const n = name.toLowerCase();
          const femaleKeywords = [
            'female', 'woman', 'girl', 'child', 'zira', 'samantha', 'vicky', 'serena', 'karen', 
            'moira', 'tessa', 'victoria', 'hazel', 'susan', 'catherine', 'fiona', 'veena', 
            'leena', 'heera', 'swara', 'aditi', 'lekha', 'geeta', 'shruti', 'jenny', 'aria', 
            'sonia', 'natasha', 'libby', 'mia', 'ava', 'steffi', 'clara', 'charlotte', 'alice', 
            'helena', 'monica', 'laura', 'julie', 'eva', 'yuki', 'ting-ting', 'kyoko', 'sin-ji', 
            'mei-jia', 'amira', 'zoe', 'salli', 'joanna', 'kendra', 'ivy', 'kimberly', 'nicole',
            'linda', 'amy', 'emma', 'olivia', 'ruth', 'elizabeth', 'sarah', 'mary'
          ];
          return femaleKeywords.some(kw => n.includes(kw));
        };

        const isMaleVoiceName = (name: string) => {
          if (isFemaleVoiceName(name)) return false;
          const n = name.toLowerCase();
          const maleKeywords = [
            'male', 'man', 'david', 'george', 'daniel', 'alex', 'tom', 'mark', 'guy', 
            'arthur', 'aaron', 'richard', 'james', 'john', 'peter', 'paul', 'rishi', 
            'ravi', 'neel', 'deep', 'baritone', 'bold', 'calm', 'professor', 'brian', 
            'russell', 'oliver', 'fred', 'lee', 'natural (male)', 'standard-b',
            'standard-d', 'wavenet-b', 'wavenet-d', 'neural2-d', 'neural2-j', 'journey-d'
          ];
          return maleKeywords.some(kw => n.includes(kw));
        };

        const langVoices = voices.filter(v => v.lang.startsWith(langPrefix));
        const hasMaleSpecificVoice = langVoices.some(v => isMaleVoiceName(v.name));

        // Marathi fallback: If no clear MALE Marathi voice, try Hindi male
        if (!hasMaleSpecificVoice && langPrefix === 'mr') {
           searchPrefix = 'hi'; 
        }

        // STRICT FILTERING: Only male/man voices
        let filteredVoices = voices.filter(v => v.lang.startsWith(searchPrefix) && !isFemaleVoiceName(v.name));
        if (filteredVoices.length === 0 && searchPrefix !== 'en') {
          // If no male found, try any non-female voice for the language
          filteredVoices = voices.filter(v => v.lang.startsWith(searchPrefix));
        }

        const isIndic = ['hi', 'mr', 'bn', 'or', 'ta', 'te', 'gu', 'kn', 'ml', 'pa'].includes(langPrefix);
        const fallbackIndicMale = isIndic && searchPrefix !== 'hi' ? voices.find(v => v.lang.startsWith('hi') && isMaleVoiceName(v.name)) : null;

        const voice = filteredVoices.find(v => isMaleVoiceName(v.name))
                   || filteredVoices.find(v => v.name.toLowerCase().includes('google') && !isFemaleVoiceName(v.name))
                   || filteredVoices.find(v => v.name.toLowerCase().includes('deep') || v.name.toLowerCase().includes('bold'))
                   || filteredVoices[0]
                   || fallbackIndicMale
                   || (langPrefix === 'en' ? voices.find(v => v.lang.startsWith('en') && isMaleVoiceName(v.name)) : null);
        
        if (voice) {
          utterance.voice = voice;
        } else {
          // Absolute safety fallback: Never pick female, and for non-English, prefer same langPrefix
          const fallbackSameLang = voices.find(v => v.lang.startsWith(langPrefix) && !isFemaleVoiceName(v.name));
          const fallbackMale = fallbackSameLang || (langPrefix === 'en' ? voices.find(v => isMaleVoiceName(v.name)) : null);
          if (fallbackMale) utterance.voice = fallbackMale;
        }

        utterance.onstart = () => {
          setIsSpeaking(true);
          // Set to start of chunk immediately
          setCurrentWordCharIndex(chunkOffsets[chunkIdx] + localCharOffset);
          lastBoundaryUpdateRef.current = Date.now();
        };

        utterance.onboundary = (event) => {
          if (event.name === 'word') {
            const globalIndex = chunkOffsets[chunkIdx] + localCharOffset + event.charIndex;
            setCurrentWordCharIndex(globalIndex);
            lastBoundaryUpdateRef.current = Date.now();
          }
        };

        utterance.onend = () => {
          clearTimeout(watchdog);
          if (utteranceRef.current === utterance && isPlayingRef.current && currentStepIndexRef.current === index) {
            playChunk(chunkIdx + 1);
          }
        };

        const startTimeMs = Date.now();
        // Watchdog: If onend doesn't fire within a reasonable time (2x expected duration), force next chunk
        const charPerSecond = 8; // Conservative estimate for Bengali/Indic
        const expectedDurationMs = (chunkText.length / charPerSecond) * 1000 + 3000;
        
        const watchdog = setTimeout(() => {
          if (utteranceRef.current === utterance && isPlayingRef.current && currentStepIndexRef.current === index) {
            console.warn("SpeechSynthesis watchdog triggered - onend likely missed");
            playChunk(chunkIdx + 1);
          }
        }, expectedDurationMs);

        utterance.onerror = (err: any) => {
          clearTimeout(watchdog);
          const errorType = err.error || err;
          
          // These are expected when we cancel/pause or scrub, so don't log them as errors
          if (errorType === 'interrupted' || errorType === 'canceled') {
            return;
          }

          console.error("SpeechSynthesis chunk error:", errorType);
          if (isPlayingRef.current && currentStepIndexRef.current === index) {
            window.speechSynthesis.cancel();
            setTimeout(() => playChunk(chunkIdx + 1), 200);
          }
        };

        // Ensure clean state
        if (window.speechSynthesis.paused) {
          window.speechSynthesis.resume();
        }
        
        window.speechSynthesis.speak(utterance);
      };

      // Determine starting chunk
      let startChunkIdx = 0;
      let startCharInChunk = 0;
      if (startTime > 0) {
        const targetGlobalChar = (startTime / duration) * fullText.length;
        for (let i = 0; i < chunkOffsets.length; i++) {
          if (i === chunkOffsets.length - 1 || (targetGlobalChar >= chunkOffsets[i] && targetGlobalChar < chunkOffsets[i+1])) {
            startChunkIdx = i;
            startCharInChunk = targetGlobalChar - chunkOffsets[i];
            break;
          }
        }
      }

      // Reset sync state
      setCurrentWordCharIndex(chunkOffsets[startChunkIdx] + startCharInChunk);
      setIsSpeaking(true); 
      
      playChunk(startChunkIdx, startCharInChunk);

      // Simulate progress for timeline
      let startRealTime = Date.now() - (startTime * 1000);
      const interval = setInterval(() => {
        if (!isPlayingRef.current || currentStepIndexRef.current !== index) {
          clearInterval(interval);
          return;
        }
        if (window.speechSynthesis.paused) {
          startRealTime = Date.now() - (currentAudioTime * 1000);
          return;
        }
        const elapsed = (Date.now() - startRealTime) / 1000;
        setCurrentAudioTime(Math.min(elapsed, duration));

        if (elapsed >= duration && !window.speechSynthesis.speaking) {
          clearInterval(interval);
        }
      }, 50);
    } else {
      setIsPlaying(false);
    }
  };

  useEffect(() => {
    // Eagerly pre-load all step audio in background to eliminate pauses and ensure seamless audio playback
    if (activeData && activeData.steps && activeData.steps.length > 0) {
      activeData.steps.forEach((step, idx) => {
        const text = step.audio_explanation || step.explanation;
        if (text && !stepAudioUrls[idx]) {
          getElevenLabsAudio(text, idx).catch(() => {});
        }
      });
    }
  }, [activeData, language]);

  const togglePlay = () => {
    if (!hasStarted) {
      setHasStarted(true);
      setIsPlaying(true);
      isPlayingRef.current = true;
      playStep(currentStepIndex, currentAudioTime);
      return;
    }
    if (isPlaying) {
      setIsPlaying(false);
      isPlayingRef.current = false;
      if (avatarVideoRef.current) avatarVideoRef.current.pause();
      if ('speechSynthesis' in window) {
        window.speechSynthesis.cancel();
      }
      if (audioRef.current) {
        audioRef.current.pause();
      }
      setIsSpeaking(false);
    } else {
      setIsPlaying(true);
      isPlayingRef.current = true;
      if (avatarVideoRef.current) avatarVideoRef.current.play().catch(() => {});
      
      // Only call playStep if we don't already have an audio element to resume
      // (The useEffect will resume audioRef.current automatically)
      if (!audioRef.current) {
        playStep(currentStepIndex, currentAudioTime);
      }
    }
  };

  const reset = () => {
    setIsPlaying(false);
    setHasStarted(false);
    setIsSpeaking(false);
    setCurrentStepIndex(0);
    setCurrentAudioTime(0);
    if (avatarVideoRef.current) {
      avatarVideoRef.current.pause();
      avatarVideoRef.current.currentTime = 0;
    }
    if ('speechSynthesis' in window) window.speechSynthesis.cancel();
  };

  const totalDuration = stepDurations.reduce((a, b) => a + b, 0);
  const currentGlobalTime = stepDurations.slice(0, currentStepIndex).reduce((a, b) => a + b, 0) + currentAudioTime;
  const progressPercentage = totalDuration > 0 ? (currentGlobalTime / totalDuration) * 100 : ((currentStepIndex + 1) / activeData.steps.length) * 100;

  const formatTime = (seconds: number) => {
    if (isNaN(seconds) || seconds < 0) return "0:00";
    const m = Math.floor(seconds / 60);
    const s = Math.floor(seconds % 60);
    return `${m}:${s.toString().padStart(2, '0')}`;
  };

  const handleTimelineScrub = (percentage: number) => {
    let targetStepIndex = 0;
    let targetAudioTime = 0;
    
    if (totalDuration > 0) {
      const targetTime = percentage * totalDuration;
      let accumulatedTime = 0;
      for (let i = 0; i < stepDurations.length; i++) {
        if (accumulatedTime + stepDurations[i] >= targetTime) {
          targetStepIndex = i;
          targetAudioTime = targetTime - accumulatedTime;
          break;
        }
        accumulatedTime += stepDurations[i];
      }
    } else {
      targetStepIndex = Math.floor(percentage * activeData.steps.length);
    }
    
    const validIndex = Math.max(0, Math.min(targetStepIndex, activeData.steps.length - 1));
    
    setCurrentStepIndex(validIndex);
    setCurrentAudioTime(targetAudioTime);
    
    if (isPlaying) {
      if ('speechSynthesis' in window) window.speechSynthesis.cancel();
      setIsSpeaking(false);
      playStep(validIndex, targetAudioTime, true);
    } else {
      if ('speechSynthesis' in window) window.speechSynthesis.cancel();
      playStep(validIndex, targetAudioTime, false);
    }
  };

  const handleTimelineChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = parseFloat(e.target.value);
    setDragPercentage(val);
  };

  const handleTimelineCommit = () => {
    setIsDragging(false);
    handleTimelineScrub(dragPercentage / 100);
  };

  const jump = (seconds: number) => {
    if (totalDuration <= 0) return;
    const newTime = Math.max(0, Math.min(currentGlobalTime + seconds, totalDuration));
    handleTimelineScrub(newTime / totalDuration);
  };

  const handleTimelineStart = () => {
    setIsDragging(true);
    setDragPercentage(progressPercentage);
  };

  const currentStep = activeData.steps[currentStepIndex];

  const preprocessContent = (content: string) => {
    if (!content) return '';
    
    // 1. Strip out the corrupted \b or \\b tokens mentioned by user, but protect LaTeX commands starting with \b
    // These often appear when word boundaries are incorrectly escaped in JSON/Strings
    let fixed = content
      .replace(/\\b(?![a-zA-Z])/g, '')
      .replace(/[\b]/g, '');

    // 2. Normalize and fix common LaTeX escaping issues
    fixed = fixed
      .replace(/\f/g, '\\frac')
      .replace(/\t/g, '\\tau')
      .replace(/\n/g, ' ')
      .replace(/\r/g, '');
      
    // 3. Ensure math delimiters are standard for the renderer
    return fixed
      .replace(/\\\[/g, '$$$$')
      .replace(/\\\]/g, '$$$$')
      .replace(/\\\(/g, '$')
      .replace(/\\\)/g, '$');
  };

  const formatLatex = (content: string, inline: boolean = true) => {
    if (!content) return '';
    return formatLatexString(content, inline);
  };

  return (
    <div ref={playerRef} className="w-full h-[960px] relative flex flex-col gap-6 pt-14 pb-8 px-12 rounded-none font-sans overflow-hidden bg-[#05050a]">
      
      {/* Top Bar (Question) - Fixed Height */}
      <div className="flex flex-row flex-shrink-0 h-[120px]">
        <div className="bg-indigo-950 text-white p-6 flex-1 flex items-center justify-between gap-6 rounded-[32px] shadow-sm border border-indigo-800 overflow-hidden">
          <div className="flex items-center gap-6 overflow-hidden">
            <div className="bg-white/20 p-4 rounded-2xl flex-shrink-0 shadow-inner"><GraduationCap className="w-10 h-10" /></div>
            <div className="flex flex-col gap-1 overflow-hidden">
              <span className="font-black text-sm tracking-[0.2em] uppercase opacity-70">
                {activeData.is_doubt_mode ? "DOUBT RESOLUTION" : "QUESTION"}
              </span>
              <span className="font-bold italic text-2xl leading-tight line-clamp-2 overflow-hidden text-ellipsis">
                <ErrorBoundary fallback={<span>{activeData.question}</span>}>
                  <Latex strict={false}>{activeData.question}</Latex>
                </ErrorBoundary>
              </span>
            </div>
          </div>
          {onReset && (
            <button 
              onClick={onReset}
              className="flex-shrink-0 text-sm font-bold bg-white/10 hover:bg-white/20 text-white px-8 py-4 rounded-2xl transition-colors border border-white/10"
            >
              Ask Another
            </button>
          )}
        </div>
      </div>

      {/* Middle Section (Professor + Board) */}
      <div className="flex flex-row gap-6 items-stretch flex-1 min-h-0 overflow-hidden">
        
        {/* Professor Card - Fixed Width */}
        <div className="w-80 bg-slate-800 rounded-[40px] shadow-sm border border-slate-700 p-5 flex flex-col items-center justify-start gap-5 flex-shrink-0 h-full">
          {/* Topic Box - Green Theme */}
          <div className="w-full bg-emerald-900/30 rounded-2xl shadow-sm relative overflow-hidden flex items-center p-4 border border-emerald-800/50 flex-shrink-0 h-[80px]">
            <div className="absolute left-0 top-0 bottom-0 w-1.5 bg-emerald-500"></div>
            <div className="flex gap-2 items-center w-full pl-2">
              <div className="flex-1 min-w-0">
                <div className="text-emerald-400 text-xs font-bold tracking-widest uppercase mb-1">
                  Topic
                </div>
                <div className="text-emerald-200 font-bold text-base leading-tight truncate">
                  {activeData.topic}
                </div>
              </div>
            </div>
          </div>

          <div 
            className="relative w-full aspect-[9/12] rounded-3xl overflow-hidden border-4 border-slate-900 shadow-inner bg-slate-800 cursor-pointer group flex-shrink-0"
            onClick={togglePlay}
          >
             <ErrorBoundary fallback={<div className="w-full h-full flex items-center justify-center text-slate-400"><GraduationCap className="w-16 h-16" /></div>}>
               <Avatar2D ref={avatarVideoRef} isSpeaking={isPlaying} videoSrc="/xai.mp4" />
             </ErrorBoundary>
          </div>

          {/* Compact Timeline Bar - Moved to Sidebar */}
          <div className="flex flex-col gap-4 w-full px-5 py-6 bg-slate-900/50 rounded-3xl border border-slate-700/50 mt-auto flex-shrink-0">
            <div className="flex flex-col items-center gap-4 mb-2">
              <div className="text-white text-xl font-black uppercase tracking-[0.2em]">Professor X</div>
              <div className="flex items-center justify-center gap-6 w-full">
                <button 
                  onClick={() => jump(-10)}
                  className="w-12 h-12 bg-slate-800 hover:bg-slate-700 text-white rounded-2xl flex items-center justify-center transition-colors border border-slate-700 shadow-sm text-sm font-bold"
                  title="Jump back 10s"
                >
                  -10
                </button>
                <button 
                  onClick={() => {
                    const isLastStep = currentStepIndex === activeData.steps.length - 1;
                    const isFinished = isLastStep && currentAudioTime >= (stepDurations[currentStepIndex] || 5);
                    if (isFinished) {
                      // Restart current content (main or doubt)
                      setCurrentStepIndex(0);
                      setCurrentAudioTime(0);
                      setCurrentWordCharIndex(0);
                      setIsPlaying(true);
                      isPlayingRef.current = true;
                      hasIntroPlayedRef.current = false;
                      playStep(0, 0, true);
                    } else {
                      togglePlay();
                    }
                  }}
                  className="w-16 h-16 bg-indigo-600 hover:bg-indigo-500 text-white rounded-2xl flex items-center justify-center transition-all shadow-lg hover:scale-105 active:scale-95"
                >
                  {(() => {
                    const isLastStep = currentStepIndex === activeData.steps.length - 1;
                    const isFinished = isLastStep && currentAudioTime >= (stepDurations[currentStepIndex] || 5);
                    if (isFinished) return <RotateCcw className="w-8 h-8" />;
                    return isPlaying ? <Pause className="w-8 h-8" /> : <Play className="w-8 h-8 ml-1" />;
                  })()}
                </button>
                <button 
                  onClick={() => jump(10)}
                  className="w-12 h-12 bg-slate-800 hover:bg-slate-700 text-white rounded-2xl flex items-center justify-center transition-colors border border-slate-700 shadow-sm text-sm font-bold"
                  title="Jump forward 10s"
                >
                  +10
                </button>
              </div>
            </div>

            <div className="flex items-center justify-between gap-3 px-1">
              <div className="text-xs font-mono text-slate-400 font-medium flex-shrink-0 whitespace-nowrap">
                {formatTime(isDragging ? (dragPercentage / 100) * totalDuration : currentGlobalTime)} / {formatTime(totalDuration)}
              </div>
            </div>
            
            <div className="w-full h-2 bg-slate-700 rounded-full relative overflow-hidden group cursor-pointer">
              <input
                type="range"
                min={0}
                max={100}
                step={0.1}
                value={isDragging ? dragPercentage : progressPercentage}
                onPointerDown={handleTimelineStart}
                onChange={handleTimelineChange}
                onPointerUp={handleTimelineCommit}
                onTouchStart={handleTimelineStart}
                onTouchEnd={handleTimelineCommit}
                className="absolute inset-0 w-full h-full opacity-0 cursor-pointer z-10"
              />
              <div 
                className="absolute left-0 top-0 bottom-0 bg-indigo-500 transition-all duration-100" 
                style={{ width: `${isDragging ? dragPercentage : progressPercentage}%` }} 
              />
            </div>
          </div>
        </div>

        {/* The Board + Visual Panel Container */}
        <div className="flex-1 flex flex-row gap-6 min-h-0 min-w-0 overflow-hidden pr-1">
          {/* Left Panel - Mathematical Solution */}
          <div className={`${activeData.diagram_needed ? 'flex-1' : 'w-full'} flex flex-col gap-6 min-h-0 flex-shrink-0 transition-all duration-500`}>
            <div 
              ref={whiteboardRef} 
              className="whiteboard-main-container flex-1 bg-slate-950 rounded-[48px] border-[16px] border-slate-800 relative overflow-hidden shadow-sm flex flex-col"
            >
              {/* Dot Grid */}
              <motion.div 
                className="absolute inset-0 opacity-30 pointer-events-none" 
                animate={{ backgroundPosition: isSpeaking ? ["0px 0px", "30px 30px"] : "0px 0px" }}
                transition={{ repeat: Infinity, duration: 4, ease: "linear" }}
                style={{ backgroundImage: 'radial-gradient(#334155 2px, transparent 2px)', backgroundSize: '30px 30px' }}
              />


              
              <div className="relative z-10 flex-1 px-12 py-6 flex flex-col w-full min-h-0 overflow-hidden">
                 {!hasStarted ? (
                   <div className="flex-1 flex flex-col items-center justify-center w-full h-full">
                     <button 
                       onClick={togglePlay}
                       className="w-32 h-32 bg-[#7b5eea] text-white rounded-full flex items-center justify-center shadow-xl hover:scale-105 transition-transform"
                     >
                       <Play className="w-16 h-16 ml-2" />
                     </button>
                     <p className="mt-10 text-slate-200 font-bold text-4xl">Press Play to Start</p>
                   </div>
                 ) : (
                   <div className="relative flex-1 flex flex-col justify-start items-start w-full min-h-0">
                      <AnimatePresence>
                        {isVisualShowcase ? (
                          <motion.div 
                            key={`showcase-${currentStepIndex}`}
                            initial={{ opacity: 0, scale: 0.98 }}
                            animate={{ opacity: 1, scale: 1 }}
                            exit={{ opacity: 0, scale: 1.02 }}
                            transition={{ duration: 0.3, ease: "easeOut" }}
                            className="absolute inset-0 flex flex-col items-center justify-center p-12 bg-slate-950 z-20"
                          >
                            <div className="flex-1 w-full flex items-center justify-center overflow-hidden">
                              {activeData.steps[currentStepIndex]?.diagram_svg ? (
                                <div 
                                  className="w-full h-full flex items-center justify-center [&>svg]:w-full [&>svg]:h-auto [&>svg]:max-h-full"
                                  dangerouslySetInnerHTML={{ __html: activeData.steps[currentStepIndex].diagram_svg }}
                                />
                              ) : ((activeData.steps[currentStepIndex] as any)?.image_url || (activeData.steps[currentStepIndex] as any)?.static_image_url) ? (
                                <img 
                                  src={(activeData.steps[currentStepIndex] as any).image_url || (activeData.steps[currentStepIndex] as any).static_image_url} 
                                  alt={activeData.steps[currentStepIndex]?.diagram_description || "Step Visual"}
                                  className="max-w-full max-h-full object-contain rounded-3xl shadow-2xl"
                                  referrerPolicy="no-referrer"
                                  onError={() => {
                                    const src = (activeData.steps[currentStepIndex] as any).image_url || (activeData.steps[currentStepIndex] as any).static_image_url;
                                    if (src) handleImageError(src);
                                  }}
                                />
                              ) : activeData.static_image_url ? (
                                <img 
                                  src={activeData.static_image_url} 
                                  alt="Resource Visual"
                                  className="max-w-full max-h-full object-contain rounded-3xl shadow-2xl"
                                  referrerPolicy="no-referrer"
                                  onError={() => {
                                    if (activeData.static_image_url) handleImageError(activeData.static_image_url);
                                  }}
                                />
                              ) : null}
                            </div>
                            <div className="mt-10 border-l-4 border-indigo-500 pl-8 w-full max-w-3xl">
                              <p className="text-indigo-200 text-[1.2rem] font-bold italic tracking-wide">
                                {activeData.steps[currentStepIndex]?.diagram_description || activeData.diagram_description || "Visualizing concept..."}
                              </p>
                            </div>
                          </motion.div>
                        ) : (
                          <motion.div 
                            key={`board-${currentStepIndex}`}
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            transition={{ duration: 0.3 }}
                            className="absolute inset-0 flex flex-col justify-start items-start w-full min-h-0"
                          >
                            <div 
                              ref={textAreaRef}
                              className="relative flex flex-col w-full h-full min-h-0 items-start justify-start overflow-hidden"
                            >
                              <div className="flex flex-col gap-4 pt-0 pb-6 w-full items-start relative h-full">
                                <StructuredSolutionArea
                                  step={currentStep}
                                  stepIndex={currentStepIndex}
                                  className="w-full h-full"
                                />
                              </div>
                            </div>
                          </motion.div>
                        )}
                </AnimatePresence>
              </div>
            )}
          </div>
        </div>

            {/* Explanation Box - Resized & Centered Content */}
            <div className="w-full h-[92px] relative flex flex-col items-center justify-center px-10 bg-red-950 rounded-[32px] border border-red-900/50 flex-shrink-0 transition-all duration-500 overflow-hidden">
              <div className="absolute left-0 top-0 bottom-0 w-2.5 bg-red-500"></div>
              <div className="pl-2 flex items-center justify-center h-full w-full" id="explanation-scroll-container">
                <div className="text-slate-200 font-montserrat font-bold text-2xl leading-tight w-full text-center flex items-center justify-center h-full">
                  <ErrorBoundary fallback={<span className="text-red-500">Error</span>}>
                    <div className="flex flex-wrap gap-x-2 text-center justify-center items-center content-center max-h-full">
                      {(() => {
                        const rawText = currentStep?.audio_explanation || currentStep?.explanation || "...";
                        const fullText = cleanCaptionText(rawText, language);
                        
                        const maxChars = 75;
                        const words = fullText.split(/(\s+)/);
                        const displayChunks: { text: string; start: number; end: number }[] = [];
                        
                        let currentChunkWords: string[] = [];
                        let currentChunkLen = 0;
                        let runningPos = 0;
                        let chunkStartPos = 0;

                        for (let i = 0; i < words.length; i++) {
                          const word = words[i];
                          const isPunctuationEnd = /[.!?।॥؟。]\s*$/.test(word);

                          if (currentChunkLen + word.length > maxChars && currentChunkWords.length > 0) {
                            const chunkText = currentChunkWords.join('');
                            displayChunks.push({
                              text: chunkText,
                              start: chunkStartPos,
                              end: runningPos
                            });
                            currentChunkWords = [];
                            currentChunkLen = 0;
                            chunkStartPos = runningPos;
                          }

                          currentChunkWords.push(word);
                          currentChunkLen += word.length;
                          runningPos += word.length;

                          if (isPunctuationEnd && currentChunkLen >= 35 && i < words.length - 1) {
                            const chunkText = currentChunkWords.join('');
                            displayChunks.push({
                              text: chunkText,
                              start: chunkStartPos,
                              end: runningPos
                            });
                            currentChunkWords = [];
                            currentChunkLen = 0;
                            chunkStartPos = runningPos;
                          }
                        }

                        if (currentChunkWords.length > 0) {
                          const chunkText = currentChunkWords.join('');
                          displayChunks.push({
                            text: chunkText,
                            start: chunkStartPos,
                            end: runningPos
                          });
                        }

                        const activeChunk = displayChunks.find(c => 
                          currentWordCharIndex >= c.start && currentWordCharIndex < c.end
                        ) || displayChunks[displayChunks.length - 1] || { text: fullText, start: 0, end: fullText.length };

                        const wordsInChunk = activeChunk.text.split(/(\s+)/);
                        let chunkCharPos = activeChunk.start;

                        // If video is finished, show the conclusion prompts
                        const isLastStep = currentStepIndex === activeData.steps.length - 1;
                        const isFinished = isLastStep && currentAudioTime >= (stepDurations[currentStepIndex] || 5);

                        return wordsInChunk.map((word, i) => {
                          const isSpace = /^\s+$/.test(word);
                          const start = chunkCharPos;
                          chunkCharPos += word.length;
                          
                          if (isSpace) return <span key={i}>{word}</span>;

                          const isCurrentWord = currentWordCharIndex >= start && currentWordCharIndex < chunkCharPos;

                          return (
                            <motion.span 
                              key={i}
                              id={isCurrentWord ? "active-word" : undefined}
                              initial={{ opacity: 0, y: 5 }}
                              animate={{ 
                                opacity: 1,
                                y: 0,
                                color: "#ffffff"
                              }}
                              transition={{ duration: 0.25 }}
                              className="inline-block"
                            >
                              {word}
                            </motion.span>
                          );
                        });
                      })()}
                    </div>
                  </ErrorBoundary>
                </div>
              </div>
            </div>
          </div>

          {/* Right Panel - Visual Representation Panel */}
          {(activeData.diagram_needed || activeData.static_image_url) && (
            <div className="w-[35%] flex flex-col gap-6 bg-slate-900 rounded-[48px] border border-slate-800 p-8 shadow-xl flex-shrink-0 min-h-0 overflow-hidden">
              <div className="flex items-center gap-4 px-2 flex-shrink-0">
                <Activity className="w-6 h-6 text-indigo-400" />
                <h3 className="text-slate-400 text-sm font-black uppercase tracking-[0.2em]">Visual Representation</h3>
              </div>
              
              <div className="flex-1 bg-slate-950/50 rounded-[32px] border border-slate-800/50 relative overflow-hidden flex items-center justify-center p-6 group cursor-zoom-in min-h-0" onClick={() => setIsDiagramExpanded(true)}>
                {isVisualShowcase ? (
                  <div className="flex flex-col items-center gap-6 opacity-40">
                    <Loader2 className="w-16 h-16 text-indigo-400 animate-spin" />
                    <span className="text-lg font-bold uppercase tracking-widest text-slate-400">In Focus on Main Board</span>
                  </div>
                ) : persistentVisual?.type === 'image' ? (
                  <div className="w-full h-full bg-white/95 rounded-2xl p-3 flex items-center justify-center shadow-inner overflow-hidden">
                    <img 
                      src={persistentVisual.value} 
                      alt={activeData.topic}
                      className="w-full h-full object-contain transition-transform group-hover:scale-[1.02]"
                      referrerPolicy="no-referrer"
                      onError={() => handleImageError(persistentVisual.value)}
                    />
                  </div>
                ) : persistentVisual?.type === 'svg' ? (
                  <div 
                    className="w-full h-full flex items-center justify-center [&>svg]:w-full [&>svg]:max-h-full [&>svg]:h-auto [&>svg]:overflow-visible transition-transform group-hover:scale-[1.02]"
                    dangerouslySetInnerHTML={{ __html: persistentVisual.value }}
                  />
                ) : (
                  <div className="flex flex-col items-center gap-6 opacity-20">
                    <TrendingUp className="w-20 h-20 text-slate-400" />
                    <span className="text-lg font-bold uppercase tracking-widest">Awaiting Visual</span>
                  </div>
                )}
                
                {persistentVisual && (
                  <div className="absolute top-6 right-6 opacity-0 group-hover:opacity-100 transition-opacity bg-black/50 p-3 rounded-xl text-white">
                    <Maximize2 className="w-5 h-5" />
                  </div>
                )}
              </div>
  
              <div className="bg-slate-950/30 rounded-3xl p-6 border border-slate-800/30 flex-shrink-0 h-[120px] overflow-y-auto custom-scrollbar">
                <p className="text-white text-lg leading-relaxed italic text-center">
                  {persistentVisual?.description || "Visual representation will appear here to illustrate the concepts with proper scales and labels."}
                </p>
              </div>
            </div>
          )}
        </div>
      </div>

      <AnimatePresence>
        {isDiagramExpanded && persistentVisual && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="absolute inset-0 z-[100] flex items-center justify-center bg-black/80 backdrop-blur-sm p-4 md:p-8"
            onClick={() => setIsDiagramExpanded(false)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="relative bg-slate-900 border border-slate-800 rounded-3xl p-6 md:p-12 w-full max-w-5xl h-[90%] overflow-hidden flex flex-col items-center justify-center shadow-2xl"
              onClick={(e) => e.stopPropagation()}
            >
              <button
                onClick={() => setIsDiagramExpanded(false)}
                className="absolute top-6 right-6 p-2 bg-slate-800 hover:bg-slate-700 text-slate-300 rounded-full transition-colors z-50"
              >
                <X className="w-6 h-6" />
              </button>
              
              <div className="w-full h-full flex items-center justify-center overflow-auto">
                {persistentVisual.type === 'image' ? (
                  <div className="w-full h-full bg-white/95 rounded-2xl p-4 flex items-center justify-center shadow-inner overflow-hidden">
                    <img 
                      src={persistentVisual.value} 
                      alt={activeData.topic}
                      className="max-w-full max-h-full object-contain"
                      referrerPolicy="no-referrer"
                      onError={() => handleImageError(persistentVisual.value)}
                    />
                  </div>
                ) : (
                  <div 
                    className="w-full h-full flex items-center justify-center [&>svg]:w-full [&>svg]:h-auto [&>svg]:max-h-full"
                    dangerouslySetInnerHTML={{ __html: persistentVisual.value }}
                  />
                )}
              </div>
              
              {persistentVisual.description && (
                <p className="mt-8 text-slate-400 text-center text-lg font-medium max-w-3xl leading-relaxed italic border-t border-slate-800 pt-6">
                  {persistentVisual.description}
                </p>
              )}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
